import React, { useState } from 'react';
import {
  FaFileInvoiceDollar, FaShieldAlt, FaPercentage,
  FaChartLine, FaCheckCircle, FaChevronDown, FaBuilding, FaGlobe
} from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
const TaxPlanning = () => {
  // State for Accordion FAQ toggling
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      q: "What is the difference between Tax Saving and Tax Planning?",
      a: "Tax saving usually involves last-minute investments to reduce taxable income before the financial year ends. Tax planning, on the other hand, is a proactive, year-round strategy that aligns your investments, expenses, and asset allocations to minimize tax liability legally while maximizing long-term wealth creation."
    },
    {
      q: "When should I start tax planning for the financial year?",
      a: "The ideal time to start tax planning is at the beginning of the financial year (April). Early planning allows you to spread out tax-saving investments systematically, optimize capital gains, and choose the most beneficial tax regime."
    },
    {
      q: "How can Lotoswealth help reduce Capital Gains Tax on stocks or real estate?",
      a: "We analyze your portfolio to identify opportunities for tax-loss harvesting, strategic holding period management (Short-Term vs. Long-Term), re-investment under available tax exemption provisions, and optimal asset allocation."
    },
    {
      q: "Do you offer tax planning for NRIs and foreign investments?",
      a: "Yes. We specialize in NRI tax advisory, including Double Taxation Avoidance Agreements (DTAA), foreign asset reporting, repatriation strategies, and tax-efficient portfolio management for non-resident investors."
    }
  ];

  return (
    <main className="tax-planning-page">

      <Helmet>
        {/* Basic SEO */}
        <title>Tax Planning Services in Hyderabad | Tax Saving & Financial Planning | Lotoswealth</title>

        <meta
          name="description"
          content="Lotoswealth provides expert Tax Planning Services in Hyderabad to help individuals, professionals, NRIs, and businesses reduce tax liabilities, maximize tax savings, and achieve long-term financial goals through strategic tax planning and investment solutions."
        />

        <meta
          name="keywords"
          content="Tax Planning Services Hyderabad, Tax Planning Hyderabad, Tax Saving Hyderabad, Income Tax Planning Hyderabad, Financial Planning Hyderabad, Tax Advisor Hyderabad, Tax Consultant Hyderabad, Wealth Management Hyderabad, Investment Planning Hyderabad, Capital Gains Tax Planning Hyderabad, Retirement Tax Planning Hyderabad, Corporate Tax Planning Hyderabad, NRI Tax Planning Hyderabad, Estate Tax Planning Hyderabad, Tax Efficient Investments Hyderabad, ELSS Investment Hyderabad, Tax Saving Mutual Funds Hyderabad, Wealth Advisory Hyderabad, Financial Advisor Hyderabad, Lotoswealth"
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Lotoswealth" />
        <meta name="language" content="English" />

        <link rel="canonical" href="https://www.lotoswealth.com/tax-planning" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Lotoswealth" />
        <meta property="og:url" content="https://www.lotoswealth.com/tax-planning" />
        <meta
          property="og:title"
          content="Tax Planning Services in Hyderabad | Lotoswealth"
        />
        <meta
          property="og:description"
          content="Save more with expert Tax Planning Services in Hyderabad. Lotoswealth helps individuals and businesses optimize taxes through smart financial planning, tax-efficient investments, and wealth management."
        />
        <meta
          property="og:image"
          content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Tax Planning Services in Hyderabad | Lotoswealth"
        />
        <meta
          name="twitter:description"
          content="Expert Tax Planning Services in Hyderabad for individuals, professionals, NRIs, and businesses. Reduce tax liabilities and maximize tax savings with Lotoswealth."
        />
        <meta
          name="twitter:image"
          content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
        />
      </Helmet>

      {/* =========================================
          1. HERO SECTION (With Ticker & Slow Zoom)
      ========================================= */}
      <div className="hero-wrapper position-relative" style={{ height: '100vh', minHeight: '800px', overflow: 'hidden' }}>

        <div className="position-absolute top-0 start-0 w-100 h-100 z-0 animate-slow-zoom">
          <img
            src="/images/tax-planning.jpg"
            alt="Tax Planning Services"
            className="w-100 h-100 object-fit-cover"
          />
        </div>

        {/* Slanted Panels Overlay */}
        <div className="panels-container position-absolute top-0 start-0 w-100 h-100 z-1 d-flex">
          <div className="slanted-panel panel-1"></div>
          <div className="slanted-panel panel-2"></div>
          <div className="slanted-panel panel-3"></div>
          <div className="slanted-panel panel-4"></div>
        </div>

        {/* --- Floating Main Header Text (Perfectly Centered) --- */}
        <div className="container hero-main-text text-center position-absolute top-50 start-50 translate-middle z-3 w-100 px-4">
          <h1 className="fw-bold display-4 animate-slide-up delay-1 mb-3 text-white" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
            Strategic Tax Planning & Advisory
          </h1>
          <h2 className="animate-slide-up delay-2 fw-bold text-white mb-4" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Maximize Your Savings & Preserve Your Wealth
          </h2>
          <p className="hero-description animate-slide-up delay-3 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Welcome to Lotoswealth, your trusted partner for strategic tax planning and advisory services in Hyderabad and across India. We provide comprehensive, 100% compliant tax strategies designed to lower your tax burden, optimize returns, and protect your hard-earned wealth.
          </p>
          <p className="hero-description mt-3 animate-slide-up delay-4 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Effective tax management is an essential element of wealth building. Rather than scrambling for last-minute deductions, our experienced financial advisors help you build a proactive, year-round tax roadmap that integrates seamlessly with your broader financial and investment goals.
          </p>
          <p className="hero-description mt-3 animate-slide-up delay-5 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Whether you are a salaried professional, business owner, NRI, or High Net Worth Individual, Lotoswealth delivers customized tax solutions to help you keep more of what you earn.
          </p>
        </div>

        {/* --- Scrolling Stock Ticker --- */}
        <div className="stock-ticker position-absolute bottom-0 w-100 z-3 bg-dark text-white border-top border-bottom border-secondary py-2" style={{ opacity: 0.9 }}>
          <div className="ticker-track">
            {/* First Set */}
            <div className="ticker-item text-white border-end border-secondary"><strong>APOLLOHOSP</strong> 8,894.00 <span className="text-danger">-63.00 (-0.70%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>ASIANPAINT</strong> 2,768.60 <span className="text-success">21.30 (0.78%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>AXISBANK</strong> 1,250.00 <span className="text-success">20.50 (1.67%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJ-AUTO</strong> 11,519.00 <span className="text-danger">-2.00 (-0.02%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJFINSV</strong> 2,085.60 <span className="text-success">56.50 (2.78%)</span></div>

            {/* Duplicated Set for Infinite Scrolling */}
            <div className="ticker-item text-white border-end border-secondary"><strong>APOLLOHOSP</strong> 8,894.00 <span className="text-danger">-63.00 (-0.70%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>ASIANPAINT</strong> 2,768.60 <span className="text-success">21.30 (0.78%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>AXISBANK</strong> 1,250.00 <span className="text-success">20.50 (1.67%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJ-AUTO</strong> 11,519.00 <span className="text-danger">-2.00 (-0.02%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJFINSV</strong> 2,085.60 <span className="text-success">56.50 (2.78%)</span></div>
          </div>
        </div>
      </div>

      {/* =========================================
          2. WHY TAX PLANNING MATTERS (Soft Teal Theme)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f0f9f6' }}>
        <div className="container py-4">
          <div className="row align-items-center g-5">
            <div className="col-lg-6 animate-slide-up delay-1">
              <h2 className="fw-bold mb-4 text-dark">
                Why Strategic Tax Planning Matters
              </h2>
              <p className="text-muted line-height-lg">
                Taxes are one of the single largest annual expenses for individuals and corporations alike. Unplanned tax obligations can quietly erode investment returns and delay long-term financial milestones.
              </p>
              <p className="text-muted line-height-lg">
                Strategic tax planning is not about tax evasion or cutting corners—it is about legally optimizing your income, investments, deductions, and capital structures to minimize tax liabilities within full compliance of tax laws.
              </p>
            </div>
            <div className="col-lg-6 animate-slide-up delay-2">
              <div className="p-4 bg-white rounded-4 shadow-sm border-start border-teal border-5">
                <h4 className="fw-bold text-teal mb-3">Key Objectives of Tax Planning</h4>
                <ul className="list-unstyled text-muted mb-0">
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Tax Liability Reduction:</strong> Legally minimizing tax payable.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Investment Optimization:</strong> Choosing tax-efficient asset classes.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Capital Gains Control:</strong> Structuring asset sales efficiently.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Seamless Compliance:</strong> Preventing penalties & notices.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          3. OUR TAX SERVICES (Crisp White)
      ========================================= */}
      <section className="py-5 bg-white" style={{ backgroundImage: "url('/images/tax-planning-bg.jpg')", backgroundSize: 'cover', backgroundPosition: 'center' }}>

        <div className="container py-4">
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-white">💼 Our Tax Planning Services</h2>
            <p className=" text-white" >End-to-end tax optimization tailored for every financial requirement.</p>
          </div>

          <div className="row g-4">

            <div className="col-lg-4 col-md-6 animate-slide-up delay-1">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaFileInvoiceDollar size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Income Tax Return (ITR) Filing</h5>
                <p className="text-muted small">Accurate, hassle-free income tax return preparation and filing for individuals, professionals, and corporate entities with complete regulatory compliance.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-2">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaPercentage size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Tax-Saving Investments</h5>
                <p className="text-muted small">Guidance on allocating funds into high-yielding, tax-efficient avenues like ELSS, NPS, Provident Funds, and specialized tax-saving bonds.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-3">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaChartLine size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Capital Gains Optimization</h5>
                <p className="text-muted small">Strategies to minimize taxes on short-term and long-term capital gains resulting from stocks, mutual funds, real estate, and business assets.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-4">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaBuilding size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Corporate Tax Advisory</h5>
                <p className="text-muted small">Comprehensive tax advisory for enterprises, startups, and SMBs to optimize corporate structures, expenses, and tax credits.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-5">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaGlobe size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">NRI Tax Advisory</h5>
                <p className="text-muted small">Cross-border tax guidance for NRIs, covering DTAA benefits, foreign income reporting, repatriation, and tax-efficient investments in India.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-6">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaShieldAlt size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Estate & Legacy Tax Planning</h5>
                <p className="text-muted small">Structuring family trusts, wills, and asset transfers to ensure smooth, tax-efficient wealth preservation across generations.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          4. OUR 5-STEP TAX PLANNING PROCESS (Dark Slate)
      ========================================= */}
      <section className="py-5 text-white" style={{ backgroundColor: '#0a192f' }}>
        <div className="container py-4">
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-white">🔄 Our Tax Planning Process</h2>
            <p className="text-light opacity-75">A systematic approach to minimizing liabilities and maximizing savings.</p>
          </div>

          <div className="row g-4">

            <div className="col-md-6 col-lg-4 animate-slide-up delay-1">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 1 – Income Assessment</h5>
                <p className="small text-muted mb-0">We conduct a detailed audit of all income sources including salary, business profits, investments, and capital gains.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4 animate-slide-up delay-2">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 2 – Regime Comparison</h5>
                <p className="small text-muted mb-0">Evaluating financial outcomes under different tax regimes to identify the most cost-effective option for your income bracket.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4 animate-slide-up delay-3">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 3 – Strategy Formulation</h5>
                <p className="small text-muted mb-0">Designing a tailored tax roadmap using permissible deductions, exemptions, tax harvesting, and tax-efficient investments.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-6 animate-slide-up delay-4">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 4 – Implementation</h5>
                <p className="small text-muted mb-0">Executing recommended tax-saving instruments, organizing documents, and filing accurate, timely tax returns.</p>
              </div>
            </div>

            <div className="col-md-12 col-lg-6 animate-slide-up delay-5">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 5 – Year-Round Review</h5>
                <p className="small text-muted mb-0">Continuously monitoring tax rule changes, mid-year income shifts, and market events to keep your tax strategy updated.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          5. WHO WE SERVE & WHY CHOOSE US (Soft Blue-Gray)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#eef2f5' }}>
        <div className="container py-4">
          <div className="row g-5">

            {/* Target Audience */}
            <div className="col-lg-5 animate-slide-up delay-1">
              <h3 className="fw-bold mb-4 text-dark">👥 Who Benefits From Our Tax Advisory?</h3>
              <p className="text-muted small mb-4">Our personalized tax strategies serve a wide array of clients:</p>
              <div className="d-flex flex-wrap gap-2">
                {[
                  "Salaried Professionals", "Business Owners & Founders",
                  "High Net Worth Individuals (HNIs)", "Doctors & Consultants",
                  "IT & Tech Professionals", "NRIs & Overseas Investors",
                  "Real Estate Investors", "Retirees & Senior Citizens"
                ].map((item, idx) => (
                  <span key={idx} className="badge bg-white text-dark border p-2 shadow-sm fs-6">
                    {item}
                  </span>
                ))}
              </div>
            </div>

            {/* Why Choose Lotoswealth */}
            <div className="col-lg-7 animate-slide-up delay-2">
              <div className="p-4 bg-white rounded-4 shadow-sm h-100">
                <h3 className="fw-bold mb-3 text-dark">🏆 Why Trust Lotoswealth for Tax Planning?</h3>
                <p className="text-muted small mb-4">Navigating complex tax landscapes requires experience, accuracy, and strategic foresight. Clients choose us because we offer:</p>
                <div className="row g-3">
                  {[
                    "Experienced Tax Advisors & Experts", "100% Legal & Compliant Tax Strategies",
                    "Proactive Year-Round Tax Planning", "Holistic Integration with Investments",
                    "Maximized Capital Gains Savings", "Transparent Advisory & Clear Pricing",
                    "Dedicated Relationship Support", "Confidential & Secure Data Handling"
                  ].map((item, idx) => (
                    <div key={idx} className="col-md-6 d-flex align-items-center">
                      <FaCheckCircle className="text-success me-2 flex-shrink-0" />
                      <span className="small text-muted fw-bold">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          6. FREQUENTLY ASKED QUESTIONS (FAQ)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container py-4" style={{ maxWidth: '850px' }}>
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-dark">❓ Frequently Asked Questions</h2>
            <p className="text-muted">Common queries regarding Tax Planning & Advisory</p>
          </div>

          <div className="d-flex flex-column gap-3 animate-slide-up delay-2">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-3 shadow-sm border overflow-hidden"
              >
                <button
                  className="w-100 text-start p-3 border-0 bg-white d-flex justify-content-between align-items-center fw-bold text-dark"
                  onClick={() => toggleFaq(index)}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="pe-3">{faq.q}</span>
                  <FaChevronDown
                    className={`text-teal transition-transform ${openFaq === index ? 'rotate-180' : ''}`}
                    style={{ transition: 'transform 0.3s ease', minWidth: '16px' }}
                  />
                </button>
                {openFaq === index && (
                  <div className="p-3 pt-0 text-muted small border-top bg-light lh-lg">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          7. FINAL CTA SECTION (Deep Navy)
      ========================================= */}
      <section className="py-5 text-white text-center" style={{ backgroundColor: '#0f1e2d' }}>
        <div className="container py-4">
          <h2 className="fw-bold mb-3 animate-slide-up delay-1">Ready to Optimize Your Taxes & Save More?</h2>
          <p className="text-light opacity-75 mb-4 animate-slide-up delay-2 mx-auto" style={{ maxWidth: '600px' }}>
            Schedule a personalized tax consultation with our expert advisors today and take control of your financial future.
          </p>
          <button className="btn-custom-teal hover-lift px-5 py-3 fs-5 border-0 rounded text-white shadow animate-slide-up delay-3">
            Book a Tax Advisory Consultation
          </button>
        </div>
      </section>

    </main>
  );
};

export default TaxPlanning;